import React, { useMemo, useState } from "react";

import { css, cx, styled } from "simplydo/react/styled";
import { Popup } from "simplydo/react/ui/popup";
import { Table, TableCell, TableRow } from "simplydo/react/ui/table";

import { useCurrentUser } from "utils/currentUser";

import { LargeCheckbox } from "components/lib/UI";

import ActionDropdown, { type Action } from "./ActionDropdown";
import type { Column, TableRowItem } from "./types";

const actionKey = "__actions";
const minCellWidth = 75;

const sizedDataCellClass = css`
  padding: 8px 0;
  background: #fff;

  min-width: ${minCellWidth}px;

  &.is-action-cell {
    min-width: 85px;
    width: 85px;

    &.dropdown-open {
      z-index: 3;
    }
  }
`;

const CellContentWrapper = styled.div<{ $lines?: number; $center?: boolean }>`
  display: ${({ $lines }) => ($lines ? "-webkit-box" : "flex")};
  flex: 1;
  justify-content: ${({ $center }) => ($center ? "center" : "flex-start")};
  align-items: ${({ $center }) => ($center ? "center" : "flex-start")};
  width: 100%;
  -webkit-line-clamp: ${({ $lines }) => ($lines ? String($lines) : "unset")};
  -webkit-box-orient: ${({ $lines }) => ($lines ? "vertical" : "initial")};
  overflow: ${({ $lines }) => ($lines ? "hidden" : "visible")};
`;

const styledRowClass = css`
  &.is-disabled {
    color: grey;
    opacity: 0.8;
    background-color: #f9f9f9;
    pointer-events: none;
  }

  &:hover {
    .${sizedDataCellClass} {
      background: #f5f5f5 !important;
    }
  }
`;

const clickEventTargetIsInteractable = (target: HTMLElement) => {
  const tagName = (target.tagName || "").toLowerCase();

  if (
    tagName === "button" ||
    tagName === "a" ||
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select" ||
    tagName === "option"
  ) {
    return true;
  }

  const clases = target.classList;
  if (
    clases.contains("popup") ||
    clases.contains("modal") ||
    clases.contains("label") ||
    clases.contains("chip")
  ) {
    return true;
  }

  if (!target.parentElement) {
    return false;
  }

  return clickEventTargetIsInteractable(target.parentElement);
};

type DataRowProps<T extends TableRowItem> = {
  onRowClick?: (item: T, event: React.MouseEvent<HTMLTableRowElement>) => void;
  item: T;
  selectable: boolean;
  selected: boolean;
  enabledColumns: Column<T>[];
  onSelect?: (item: T, checked: boolean) => void;
  actions?: Action<T>[];
  index: number;
  getRowStyle?: (item: T) => React.CSSProperties;
  getCellStyle?: (cell: Column<T>, item: T) => React.CSSProperties;
};

const DataRow = <T extends TableRowItem>({
  onRowClick,
  item,
  selectable,
  selected,
  enabledColumns,
  onSelect,
  actions = [],
  index,
  getRowStyle,
  getCellStyle,
}: DataRowProps<T>) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const actionColumnItems = useMemo(() => [item], [item]);

  const user = useCurrentUser();

  return (
    <TableRow
      className={cx(styledRowClass, item.disabled && "is-disabled")}
      style={getRowStyle ? getRowStyle(item) : {}}
      onMouseDown={() => {
        if (window.getSelection) {
          window.getSelection()?.removeAllRanges();
        }
      }}
      onClick={(event: React.MouseEvent<HTMLTableRowElement>) => {
        if (event.defaultPrevented) {
          return;
        }
        const selection = window.getSelection();
        if (selection?.toString()) {
          return;
        }
        if (clickEventTargetIsInteractable(event.target as HTMLElement)) {
          return;
        }
        onRowClick?.(item, event);
      }}
    >
      {selectable ? (
        <TableCell
          className={sizedDataCellClass}
          style={
            getCellStyle
              ? {
                  minWidth: 40,
                  ...getCellStyle({ key: "__select" } as Column<T>, item),
                }
              : { minWidth: 40 }
          }
        >
          <div
            style={{
              display: "flex",
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {!item.disabled && (
              <LargeCheckbox
                checked={selected}
                onChange={(e, checked) => {
                  e.preventDefault();
                  onSelect?.(item, checked);
                }}
              />
            )}
          </div>
        </TableCell>
      ) : null}
      {enabledColumns.map((col) => {
        const cellValue = item[col.key as keyof T];
        const content = col.render
          ? col.render({ cell: cellValue, item })
          : (cellValue as React.ReactNode);
        return (
          <TableCell
            className={cx(
              sizedDataCellClass,
              col.key === actionKey && "is-action-cell",
              col.key === actionKey && dropdownOpen && "dropdown-open",
            )}
            collapsing={col.key === actionKey || col.collapsing}
            key={col.key}
            style={{
              minWidth: col.width ?? minCellWidth,
              ...(col.style || {}),
              ...(getCellStyle ? getCellStyle(col, item) : {}),
            }}
          >
            <CellContentWrapper $lines={col.lines} $center={col.center}>
              {col.key === actionKey ? (
                <ActionDropdown
                  actions={actions}
                  items={actionColumnItems}
                  type="single"
                  user={user}
                  setDropdownOpen={setDropdownOpen}
                />
              ) : col.lines ? (
                <Popup hoverable trigger={<span>{content}</span>}>
                  {<span>{content}</span>}
                </Popup>
              ) : (
                content
              )}
            </CellContentWrapper>
          </TableCell>
        );
      })}
    </TableRow>
  );
};

export default React.memo(DataRow) as typeof DataRow;
